import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { ScreenService, ThemingService } from '@catch/services';

@Directive({
  selector: '[tooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private themingService = inject(ThemingService);
  private screenService = inject(ScreenService);

  @Input('tooltip') tooltipTitle!: string | HTMLElement;
  @Input() tooltipDisabled = false;
  @Input() position: 'top' | 'left' | 'right' | 'bottom' = 'top';
  @Input() delay = 200;
  observer: MutationObserver;
  tooltip: HTMLElement | null = null;
  offset = 10;
  timerRunning = false;
  mouseover = false;

  constructor() {
    this.observer = new MutationObserver(list => {
      for (const record of list) {
        if (
          record &&
          record.attributeName == 'disabled' &&
          record.target instanceof HTMLElement &&
          record.target.hasAttribute('disabled')
        ) {
          this.hide();
        } else if (
          record &&
          record.attributeName == 'ng-reflect-tooltip-title'
        ) {
          this.tooltip!.innerHTML = this.tooltipTitle as string;
          this.setPosition();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.hide();
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (this.tooltipDisabled) return;
    this.mouseover = true;
    if (!this.tooltip) {
      this.show();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.mouseover = false;
    if (this.tooltip) {
      this.hide();
    }
  }

  show() {
    if (this.screenService.isMobile) return;
    if (!this.tooltipTitle) return;
    if (this.timerRunning) return;
    this.timerRunning = true;
    this.create();
    this.setPosition();
    setTimeout(() => {
      this.timerRunning = false;
      if (!this.mouseover) return;
      if (this.tooltip && this.tooltip.classList)
        this.renderer.addClass(this.tooltip, 'tooltip-show');
    }, this.delay);
  }

  hide() {
    if (
      this.tooltip !== undefined &&
      this.tooltip !== null &&
      this.tooltip.classList
    )
      this.renderer.removeClass(this.tooltip, 'tooltip-show');
    setTimeout(() => {
      if (this.tooltip) {
        this.renderer.removeChild(document.body, this.tooltip);
        this.observer.disconnect();
      }
      this.tooltip = null;
    }, 100);
  }

  create() {
    const tooltip = this.renderer.createElement('span') as HTMLElement;
    this.tooltip = tooltip;
    tooltip.insertAdjacentHTML(
      'afterbegin',
      this.tooltipTitle instanceof HTMLElement
        ? this.tooltipTitle.innerHTML
        : this.tooltipTitle
    );
    this.renderer.appendChild(document.body, tooltip);
    this.renderer.addClass(tooltip, 'tooltip');
    this.renderer.addClass(tooltip, `tooltip-${this.position}`);
    if (this.themingService.theme === 'dark') {
      this.renderer.addClass(tooltip, 'darkmode');
    }
    this.observer.observe(this.el.nativeElement, {
      attributes: true,
      childList: false,
      subtree: false,
    });
  }

  setPosition() {
    const tooltip = this.tooltip;
    if (!tooltip) return;

    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = tooltip.getBoundingClientRect();
    const scrollPos =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const viewportPadding = 8;

    let top: number, left: number;

    switch (this.position) {
      case 'top': {
        top = hostPos.top - tooltipPos.height - this.offset;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      }
      case 'bottom': {
        top = hostPos.bottom + this.offset;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      }
      case 'left': {
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.left - tooltipPos.width - this.offset;
        break;
      }
      case 'right': {
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.right + this.offset;
        break;
      }
      default: {
        top = hostPos.top - tooltipPos.height - this.offset;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      }
    }

    // Keep natural width: shift to viewport edge instead of overflowing/shrinking
    const minLeft = viewportPadding;
    const maxLeft = window.innerWidth - tooltipPos.width - viewportPadding;
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

    this.renderer.setStyle(tooltip, 'top', `${top + scrollPos}px`);
    this.renderer.setStyle(tooltip, 'left', `${left}px`);
  }
}
